// Scoreboard — DOM overlay panel centered at the top of the viewport.
//
// Lives in the DOM rather than the canvas: HUD text wants crisp glyphs at
// any resolution and has no reason to ride the world renderer. Values are
// dirty-checked so `textContent` (and its layout work) is only touched when
// the displayed string actually changes.

import type { MatchSnapshot } from "@arcade-hockey/sim";

const PANEL_STYLE: Partial<CSSStyleDeclaration> = {
  position: "absolute",
  top: "12px",
  left: "50%",
  transform: "translateX(-50%)",
  width: "340px",
  height: "56px",
  background: "rgba(17, 20, 24, 0.85)",
  borderRadius: "8px",
  color: "#ffffff",
  font: "700 22px system-ui, sans-serif",
  textShadow: "0 0 8px rgba(120, 220, 255, 0.35)",
};

const HOME_STYLE: Partial<CSSStyleDeclaration> = {
  position: "absolute",
  left: "16px",
  top: "14px",
};

const AWAY_STYLE: Partial<CSSStyleDeclaration> = {
  position: "absolute",
  right: "16px",
  top: "14px",
};

const PERIOD_STYLE: Partial<CSSStyleDeclaration> = {
  position: "absolute",
  left: "50%",
  top: "6px",
  transform: "translateX(-50%)",
  fontSize: "14px",
  color: "#aaaaaa",
};

const CLOCK_STYLE: Partial<CSSStyleDeclaration> = {
  position: "absolute",
  left: "50%",
  top: "22px",
  transform: "translateX(-50%)",
  fontSize: "22px",
};

export class Scoreboard {
  readonly root: HTMLDivElement;
  private homeText: HTMLSpanElement;
  private awayText: HTMLSpanElement;
  private periodText: HTMLSpanElement;
  private clockText: HTMLSpanElement;
  // Cached display values — only write textContent on real content changes.
  private lastHomeText = "";
  private lastAwayText = "";
  private lastPeriodText = "";
  private lastClockText = "";

  constructor() {
    this.root = document.createElement("div");
    Object.assign(this.root.style, PANEL_STYLE);

    this.homeText = span("HOME 0", HOME_STYLE);
    this.awayText = span("0 AWAY", AWAY_STYLE);
    this.periodText = span("P1", PERIOD_STYLE);
    this.clockText = span("2:00", CLOCK_STYLE);
    this.root.append(this.homeText, this.awayText, this.periodText, this.clockText);
  }

  update(snap: MatchSnapshot): void {
    const homeStr = `HOME ${snap.score.home}`;
    if (homeStr !== this.lastHomeText) {
      this.homeText.textContent = homeStr;
      this.lastHomeText = homeStr;
    }
    const awayStr = `${snap.score.away} AWAY`;
    if (awayStr !== this.lastAwayText) {
      this.awayText.textContent = awayStr;
      this.lastAwayText = awayStr;
    }
    const periodStr = `P${snap.clock.periodIndex + 1}`;
    if (periodStr !== this.lastPeriodText) {
      this.periodText.textContent = periodStr;
      this.lastPeriodText = periodStr;
    }

    let clockStr: string;
    const totalSeconds = Math.max(0, Math.ceil(snap.clock.remainingTicks / 60));
    if (snap.clock.phase === "Play" || snap.clock.phase === "Faceoff") {
      const m = Math.floor(totalSeconds / 60);
      const s = totalSeconds % 60;
      clockStr = `${m}:${s.toString().padStart(2, "0")}`;
    } else if (snap.clock.phase === "GoalCelebration") {
      clockStr = "GOAL";
    } else if (snap.clock.phase === "PeriodBreak") {
      clockStr = "BREAK";
    } else if (snap.clock.phase === "GameOver") {
      clockStr = "FINAL";
    } else {
      clockStr = "—";
    }
    if (clockStr !== this.lastClockText) {
      this.clockText.textContent = clockStr;
      this.lastClockText = clockStr;
    }
  }

  destroy(): void {
    this.root.remove();
  }
}

function span(text: string, style: Partial<CSSStyleDeclaration>): HTMLSpanElement {
  const el = document.createElement("span");
  el.textContent = text;
  Object.assign(el.style, style);
  return el;
}
